// ConfirmPasswordModal — Phase 11G shared "type your password to
// authorize this action" gate. Used by all admin endpoints that the
// backend protects with `requireConfirmation()` (bulk reassign,
// settlement replay, etc.).
//
// Usage pattern:
//
//   const [confirm, setConfirm] = useState<{ onConfirm: (token: string) => Promise<void> } | null>(null);
//   ...
//   onClick={() => setConfirm({ onConfirm: async (tok) => {
//     await adminApi.post('/admin/...', body, tok);
//   } })}
//
//   {confirm && <ConfirmPasswordModal title="Bulk reassign 5 players?"
//                                     subtitle="..." dangerLabel="Reassign"
//                                     onCancel={() => setConfirm(null)}
//                                     onConfirmed={async (tok) => {
//                                       await confirm.onConfirm(tok);
//                                       setConfirm(null);
//                                     }} />}
//
// The modal hits POST /api/admin/confirm/issue { password } itself,
// then calls onConfirmed(token). The caller decides what to do with
// the token.

import { useState } from 'react';
import { motion } from 'motion/react';
import Icon from '../../components/Icon';
import { adminApi } from '../lib/adminApi';
import type { ApiError } from '../../lib/api';

export interface ConfirmPasswordModalProps {
  title: string;
  subtitle?: string;
  dangerLabel?: string;
  tone?: 'fuchsia' | 'rose' | 'amber';
  onCancel: () => void;
  onConfirmed: (confirmToken: string) => Promise<void> | void;
}

export default function ConfirmPasswordModal({
  title, subtitle, dangerLabel = 'Confirm', tone = 'fuchsia',
  onCancel, onConfirmed,
}: ConfirmPasswordModalProps) {
  const [pw, setPw] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!pw || busy) return;
    setBusy(true); setErr(null);
    try {
      const r = await adminApi.post<{ token: string; expires_at: string }>('/admin/confirm/issue', { password: pw });
      await onConfirmed(r.token);
    } catch (e) { setErr((e as ApiError).message); }
    finally { setBusy(false); }
  }

  const T = TONE[tone];

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={onCancel}>
      <motion.form onSubmit={submit} onClick={(e) => e.stopPropagation()}
                   initial={{ opacity: 0, scale: 0.95, y: 8 }} animate={{ opacity: 1, scale: 1, y: 0 }}
                   className="rounded-2xl max-w-md w-full p-5 space-y-4
                              bg-white dark:bg-neutral-900
                              border border-neutral-200 dark:border-white/10
                              shadow-2xl">
        <div className="flex items-start gap-3">
          <span className={`grid place-items-center w-10 h-10 rounded-xl shrink-0 ring-1 ${T.badge}`}>
            <Icon name="shield_lock" size={20} filled />
          </span>
          <div className="min-w-0">
            <h3 className="text-[15px] font-bold text-neutral-900 dark:text-white">{title}</h3>
            {subtitle && (
              <p className="text-[12.5px] text-neutral-600 dark:text-white/60 mt-1 leading-relaxed">{subtitle}</p>
            )}
          </div>
        </div>

        {err && (
          <div className="rounded-xl border px-3 py-2 text-[12px]
                          bg-rose-50 border-rose-200 text-rose-700
                          dark:bg-rose-900/30 dark:border-rose-800/50 dark:text-rose-200">
            {err}
          </div>
        )}

        <label className="block">
          <span className="block text-[10.5px] uppercase tracking-wider text-neutral-500 dark:text-white/55 mb-1 font-semibold">
            Re-enter your admin password
          </span>
          <input type="password" autoFocus value={pw} onChange={(e) => setPw(e.target.value)}
                 placeholder="••••••••"
                 className="w-full rounded-xl px-3 py-2 text-[14px]
                            bg-white dark:bg-neutral-950/60
                            border border-neutral-200 dark:border-neutral-800
                            text-neutral-900 dark:text-white
                            placeholder-neutral-400 dark:placeholder-neutral-500
                            outline-none focus:border-fuchsia-500 focus:ring-2 focus:ring-fuchsia-500/30" />
          <p className="text-[10.5px] text-neutral-500 dark:text-white/45 mt-1">
            Re-confirming your identity protects against accidental high-impact actions.
          </p>
        </label>

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={onCancel} disabled={busy}
                  className="px-4 py-2 rounded-full text-[12px] font-bold
                             bg-white text-neutral-700 border border-neutral-200 hover:bg-neutral-50
                             dark:bg-white/[0.04] dark:text-white/75 dark:border-white/10 dark:hover:bg-white/[0.07]
                             disabled:opacity-50">
            Cancel
          </button>
          <button type="submit" disabled={busy || !pw}
                  className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12px] font-bold transition active:scale-95 disabled:opacity-50 ${T.btn} ${T.shadow}`}>
            {busy ? <Icon name="progress_activity" size={13} className="animate-spin" /> : <Icon name="lock_open" size={13} />}
            {dangerLabel}
          </button>
        </div>
      </motion.form>
    </div>
  );
}

const TONE = {
  fuchsia: {
    badge: 'bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-400/15 dark:text-fuchsia-300 dark:ring-fuchsia-400/30',
    btn:    'bg-fuchsia-600 hover:bg-fuchsia-500 active:bg-fuchsia-700',
    shadow: 'shadow-[0_2px_10px_rgba(217,70,239,0.4)]',
  },
  rose: {
    badge: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/30',
    btn:    'bg-rose-600 hover:bg-rose-500 active:bg-rose-700',
    shadow: 'shadow-[0_2px_10px_rgba(244,63,94,0.4)]',
  },
  amber: {
    badge: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/30',
    btn:    'bg-amber-600 hover:bg-amber-500 active:bg-amber-700',
    shadow: 'shadow-[0_2px_10px_rgba(245,158,11,0.4)]',
  },
} as const;
